import { useState } from "react";
import { format, parseISO } from "date-fns";
import type { DateRange } from "react-day-picker";
import {
  SlidersHorizontal,
  ChevronRight,
  ChevronLeft,
  Check,
  X,
  Shapes,
  Users,
  Bell,
  Clock,
  CalendarDays,
  type LucideIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { UserMultiSelectList } from "@/components/ui/user-multi-select";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { ACTIVITY_CATEGORIES } from "@/domains/pm/utils/activityMeta";
import type { UserProfile } from "@/domains/pm/types";
import { pageInfoTarget } from "@/components/page-info/pageInfoTarget";
import {
  appliedCustomRange,
  hasTimeRangeFilter,
  type NotificationsCustomRange,
  type NotificationsTimeRange,
} from "@/domains/pm/utils/notificationsTimeRange";

export type { NotificationsTimeRange, NotificationsCustomRange };
/** Active filter selection for the Notifications feed. Empty arrays + false +
 *  "all" means "no filter". Kept flat so wiring into the repo opts is direct. */
export interface NotificationsFilterValue {
  categories: string[]; // ACTIVITY_CATEGORIES keys
  actorIds: number[];
  unreadOnly: boolean;
  timeRange: NotificationsTimeRange;
  customRange: NotificationsCustomRange | null;
}

export const EMPTY_NOTIFICATIONS_FILTER: NotificationsFilterValue = {
  categories: [],
  actorIds: [],
  unreadOnly: false,
  timeRange: "all",
  customRange: null,
};

export function isNotificationsFilterActive(v: NotificationsFilterValue): boolean {
  return v.categories.length > 0 || v.actorIds.length > 0 || v.unreadOnly || hasTimeRangeFilter(v);
}

type Dimension = "type" | "person" | "unread" | "timeRange";
/** The calendar is a sub-step of "timeRange", not a dimension of its own. */
type Step = Dimension | "customRange";
const DIMENSIONS: Dimension[] = ["type", "person", "unread", "timeRange"];
const DIMENSION_ICON: Record<Dimension, LucideIcon> = {
  type: Shapes,
  person: Users,
  unread: Bell,
  timeRange: Clock,
};

const TIME_RANGES: Exclude<NotificationsTimeRange, "custom">[] = ["all", "today", "7d", "30d"];

function formatDay(day: string, language: "en" | "et"): string {
  return parseISO(day).toLocaleDateString(language === "et" ? "et-EE" : "en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function userName(u: UserProfile): string {
  const n = `${u.first_name ?? ""} ${u.last_name ?? ""}`.trim();
  return n || u.email;
}

interface Props {
  users: UserProfile[];
  value: NotificationsFilterValue;
  onChange: (next: NotificationsFilterValue) => void;
}

/**
 * Cascading filter control for the Notifications feed — mirrors the /issues
 * IssueFilterControl: a "Filter" button opens a popover listing the dimensions
 * (activity type, person, unread, time range); picking one swaps to its picker.
 * Active filters render as removable chips.
 */
export function NotificationsFilterControl({ users, value, onChange }: Props) {
  const { t, language } = useLanguage();
  const f = t.pm.notifications.filter;
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<Step | null>(null);
  const [draft, setDraft] = useState<DateRange | undefined>();

  const dimensionLabel = (d: Dimension): string =>
    d === "type" ? f.type : d === "person" ? f.person : d === "unread" ? f.unread : f.timeRange;
  const categoryLabel = (key: string): string =>
    f.categories[key as keyof typeof f.categories] ?? key;
  const rangeLabel = (r: Exclude<NotificationsTimeRange, "custom">): string =>
    f.ranges[r === "7d" ? "last7" : r === "30d" ? "last30" : r];
  const customLabel = (range: NotificationsCustomRange): string =>
    range.from === range.to
      ? formatDay(range.from, language)
      : `${formatDay(range.from, language)} – ${formatDay(range.to, language)}`;

  // The applied custom range, only when the feed is actually filtered by it —
  // the check mark, label and calendar seed must agree with the chip and the
  // query (and an unparseable range never reaches `format`).
  const appliedCustom = appliedCustomRange(value);

  // Seed the calendar with the applied range so re-opening it edits, not restarts.
  const openCustomRange = () => {
    setDraft(
      appliedCustom
        ? { from: parseISO(appliedCustom.from), to: parseISO(appliedCustom.to) }
        : undefined
    );
    setStep("customRange");
  };

  const openAt = (d: Dimension) => {
    if (d === "timeRange" && value.timeRange === "custom") openCustomRange();
    else setStep(d);
    setOpen(true);
  };

  const applyCustomRange = () => {
    // Both ends, as the hint asks; one day is that day picked twice.
    if (!draft?.from || !draft.to) return;
    const [start, last] = draft.from <= draft.to ? [draft.from, draft.to] : [draft.to, draft.from];
    onChange({
      ...value,
      timeRange: "custom",
      customRange: { from: format(start, "yyyy-MM-dd"), to: format(last, "yyyy-MM-dd") },
    });
    setOpen(false);
    setStep(null);
  };

  const toggleCategory = (key: string) => {
    const has = value.categories.includes(key);
    onChange({
      ...value,
      categories: has ? value.categories.filter((k) => k !== key) : [...value.categories, key],
    });
  };

  // Build active-filter chips.
  const chips: { key: Dimension; label: string; onRemove: () => void }[] = [];
  if (value.categories.length > 0) {
    const first = categoryLabel(value.categories[0]);
    const summary = first + (value.categories.length > 1 ? ` +${value.categories.length - 1}` : "");
    chips.push({
      key: "type",
      label: `${f.type}: ${summary}`,
      onRemove: () => onChange({ ...value, categories: [] }),
    });
  }
  if (value.actorIds.length > 0) {
    const names = value.actorIds
      .map((id) => users.find((u) => u.id === id))
      .filter((u): u is UserProfile => !!u);
    const summary = names.length
      ? userName(names[0]) + (names.length > 1 ? ` +${names.length - 1}` : "")
      : String(value.actorIds.length);
    chips.push({
      key: "person",
      label: `${f.person}: ${summary}`,
      onRemove: () => onChange({ ...value, actorIds: [] }),
    });
  }
  if (value.unreadOnly) {
    chips.push({
      key: "unread",
      label: f.unreadOnly,
      onRemove: () => onChange({ ...value, unreadOnly: false }),
    });
  }
  if (hasTimeRangeFilter(value)) {
    chips.push({
      key: "timeRange",
      label: appliedCustom
        ? customLabel(appliedCustom)
        : rangeLabel(value.timeRange as Exclude<NotificationsTimeRange, "custom">),
      onRemove: () => onChange({ ...value, timeRange: "all", customRange: null }),
    });
  }

  const stepHeader = (d: Dimension, back: Dimension | null = null, label = dimensionLabel(d)) => {
    const Icon = DIMENSION_ICON[d];
    return (
      <div className="flex items-center gap-1.5 border-b px-1 py-1">
        <button
          type="button"
          onClick={() => setStep(back)}
          className="rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
          aria-label="Back"
        >
          <ChevronLeft className="h-3.5 w-3.5" />
        </button>
        <Icon className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="text-xs font-medium">{label}</span>
      </div>
    );
  };

  const checkOption = (
    key: string,
    label: string,
    active: boolean,
    onClick: () => void,
    Icon?: LucideIcon
  ) => (
    <button
      key={key}
      type="button"
      onClick={onClick}
      className="flex w-full items-center justify-between rounded px-2 py-1.5 text-xs hover:bg-muted"
    >
      <span className="flex items-center gap-2">
        {Icon && <Icon className="h-3.5 w-3.5 text-muted-foreground" />}
        {label}
      </span>
      {active && <Check className="h-3.5 w-3.5" />}
    </button>
  );

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Popover
        open={open}
        onOpenChange={(o) => {
          setOpen(o);
          if (!o) setStep(null);
        }}
        modal={true}
      >
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="control"
            className="gap-1.5"
            {...pageInfoTarget("notifications.filter")}
          >
            <SlidersHorizontal className="opacity-60" />
            {f.title}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className={step === "customRange" ? "w-auto p-0" : "w-60 p-0"}
        >
          {step === null && (
            <div className="p-1">
              {DIMENSIONS.map((d) => {
                const Icon = DIMENSION_ICON[d];
                return (
                  <button
                    key={d}
                    type="button"
                    onClick={() => setStep(d)}
                    className="flex w-full items-center justify-between rounded px-2 py-1.5 text-xs hover:bg-muted"
                  >
                    <span className="flex items-center gap-2">
                      <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                      {dimensionLabel(d)}
                    </span>
                    <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                  </button>
                );
              })}
            </div>
          )}

          {step === "type" && (
            <div>
              {stepHeader("type")}
              <div className="max-h-72 overflow-y-auto p-1">
                {ACTIVITY_CATEGORIES.map((c) =>
                  checkOption(
                    c.key,
                    categoryLabel(c.key),
                    value.categories.includes(c.key),
                    () => toggleCategory(c.key),
                    c.icon
                  )
                )}
              </div>
            </div>
          )}

          {step === "person" && (
            <div>
              {stepHeader("person")}
              <UserMultiSelectList
                users={users}
                selectedUserIds={value.actorIds}
                onChange={(ids) => onChange({ ...value, actorIds: ids })}
                searchPlaceholder={t.pm.issues.searchUsers}
                emptyMessage={t.pm.issues.noUsersFound}
              />
            </div>
          )}

          {step === "unread" && (
            <div className="p-1">
              {stepHeader("unread")}
              <div className="pt-1">
                {checkOption("unreadOnly", f.unreadOnly, value.unreadOnly, () =>
                  onChange({ ...value, unreadOnly: !value.unreadOnly })
                )}
              </div>
            </div>
          )}

          {step === "timeRange" && (
            <div className="p-1">
              {stepHeader("timeRange")}
              <div className="pt-1">
                {TIME_RANGES.map((r) =>
                  checkOption(
                    r,
                    rangeLabel(r),
                    r === "all" ? !hasTimeRangeFilter(value) : value.timeRange === r,
                    () => onChange({ ...value, timeRange: r, customRange: null })
                  )
                )}
                <button
                  type="button"
                  onClick={openCustomRange}
                  className="flex w-full items-center justify-between rounded px-2 py-1.5 text-xs hover:bg-muted"
                >
                  <span className="flex items-center gap-2">
                    <CalendarDays className="h-3.5 w-3.5 text-muted-foreground" />
                    {appliedCustom ? customLabel(appliedCustom) : f.ranges.custom}
                  </span>
                  {appliedCustom ? (
                    <Check className="h-3.5 w-3.5" />
                  ) : (
                    <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                  )}
                </button>
              </div>
            </div>
          )}

          {step === "customRange" && (
            <div>
              {stepHeader("timeRange", "timeRange", f.ranges.custom)}
              <div className="flex justify-center">
                <Calendar
                  mode="range"
                  selected={draft}
                  // Once a range is complete, the next click starts a new one
                  // at that day. react-day-picker would otherwise stretch the
                  // old range to it, or clear it when it is one of its ends.
                  onSelect={(range, day) =>
                    setDraft(draft?.from && draft.to ? { from: day, to: undefined } : range)
                  }
                  defaultMonth={draft?.from}
                  disabled={{ after: new Date() }}
                  numberOfMonths={1}
                />
              </div>
              <div className="flex items-center justify-between gap-2 border-t px-3 py-2">
                <span className="text-xs text-muted-foreground">
                  {draft?.from && draft.to
                    ? customLabel({
                        from: format(draft.from, "yyyy-MM-dd"),
                        to: format(draft.to, "yyyy-MM-dd"),
                      })
                    : f.customRangeHint}
                </span>
                <Button size="sm" onClick={applyCustomRange} disabled={!draft?.from || !draft.to}>
                  {f.apply}
                </Button>
              </div>
            </div>
          )}
        </PopoverContent>
      </Popover>

      {chips.map((chip) => (
        <span
          key={chip.key}
          className="inline-flex items-center gap-1 rounded-full border bg-muted/40 py-0.5 pl-2 pr-1 text-xs"
        >
          <button
            type="button"
            onClick={() => openAt(chip.key)}
            className="max-w-[200px] truncate hover:text-foreground"
          >
            {chip.label}
          </button>
          <button
            type="button"
            onClick={chip.onRemove}
            aria-label={`Remove ${chip.label}`}
            className="rounded-full p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="h-3 w-3" />
          </button>
        </span>
      ))}

      {chips.length >= 2 && (
        <button
          type="button"
          onClick={() => onChange(EMPTY_NOTIFICATIONS_FILTER)}
          className="inline-flex items-center gap-1 text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
        >
          {f.clearAll}
          <X className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}
